import * as React from 'react';
import { Button } from 'react-bootstrap';

// Lightweight inline markdown used by the Notes fields:
//   **bold**       -> <strong>
//   *italic*       -> <em>
//   _underline_    -> <u>
// Markers never span a newline, so a stray '*' on its own line can't run away.
// This is intentionally minimal (no nesting) — enough for practitioner notes
// while keeping the stored value a plain, human-readable string that the PDF
// packer can still line-count.

export type FormatMarker = '**' | '*' | '_';

const isAlnum = (c: string): boolean => !!c && /[A-Za-z0-9]/.test(c);

// Try to match a marker starting at index i. Returns the inner text and the
// index just past the closing marker, or null when this position isn't a valid
// span. Guards against the common false positives:
//   - a space right inside the markers ("* x *" multiplication, "** x **"),
//   - underline markers touching an alphanumeric on the outside ("T3_free_" /
//     snake_case), and
//   - a marker that would span a newline.
function matchMarker(
  text: string,
  i: number,
  marker: FormatMarker,
): { inner: string; end: number } | null {
  const n = text.length;
  const start = i + marker.length;
  let j = start;
  while (j < n) {
    const c = text[j];
    if (c === '\n') return null;
    if (marker === '**') {
      if (c === '*' && text[j + 1] === '*') break; // closing **
      if (c === '*') return null; // a lone * inside bold — not a span
    } else if (c === marker) {
      break; // closing * or _
    }
    j++;
  }
  if (j >= n) return null; // no closing marker
  const inner = text.slice(start, j);
  if (!inner || /^\s|\s$/.test(inner)) return null; // no empty / space-hugged span
  if (marker === '_') {
    const before = i > 0 ? text[i - 1] : '';
    const after = j + marker.length < n ? text[j + marker.length] : '';
    if (isAlnum(before) || isAlnum(after)) return null; // snake_case guard
  }
  return { inner, end: j + marker.length };
}

// Parse a string's inline markers into React nodes. Newlines are preserved as
// literal characters, so the surrounding element must keep `white-space:
// pre-line` for line breaks to show (same as before formatting existed).
export function renderInlineMarkdown(text: string): React.ReactNode {
  if (!text) return text;
  const nodes: React.ReactNode[] = [];
  let buf = '';
  const flush = () => {
    if (buf) {
      nodes.push(buf);
      buf = '';
    }
  };
  let i = 0;
  while (i < text.length) {
    let m: { inner: string; end: number } | null = null;
    let tag: 'strong' | 'em' | 'u' | null = null;
    if (text.startsWith('**', i)) {
      m = matchMarker(text, i, '**');
      tag = 'strong';
    }
    if (!m && text[i] === '*') {
      m = matchMarker(text, i, '*');
      tag = 'em';
    }
    if (!m && text[i] === '_') {
      m = matchMarker(text, i, '_');
      tag = 'u';
    }
    if (m) {
      flush();
      const key = `md-${i}`;
      if (tag === 'strong') nodes.push(<strong key={key}>{m.inner}</strong>);
      else if (tag === 'em') nodes.push(<em key={key}>{m.inner}</em>);
      else nodes.push(<u key={key}>{m.inner}</u>);
      i = m.end;
    } else {
      buf += text[i];
      i++;
    }
  }
  flush();
  return <>{nodes}</>;
}

// Wrap the current selection of a textarea in `marker`, returning the new value
// and the selection to restore so the wrapped text stays highlighted. With an
// empty selection it drops the markers at the caret ready to type between them.
export function wrapSelection(
  el: HTMLTextAreaElement,
  marker: FormatMarker,
): { value: string; selStart: number; selEnd: number } {
  const value = el.value ?? '';
  const s = el.selectionStart ?? value.length;
  const e = el.selectionEnd ?? value.length;
  const before = value.slice(0, s);
  const sel = value.slice(s, e);
  const after = value.slice(e);
  return {
    value: `${before}${marker}${sel}${marker}${after}`,
    selStart: s + marker.length,
    selEnd: e + marker.length,
  };
}

// Map a Bold/Italic/Underline keyboard shortcut (Cmd/Ctrl+B/I/U) to its marker,
// or null when the event isn't a formatting shortcut.
export function formatMarkerForKeydown(
  e: React.KeyboardEvent,
): FormatMarker | null {
  if (!(e.metaKey || e.ctrlKey) || e.altKey) return null;
  switch (e.key.toLowerCase()) {
    case 'b':
      return '**';
    case 'i':
      return '*';
    case 'u':
      return '_';
    default:
      return null;
  }
}

type ToolbarProps = {
  // Returns the textarea the toolbar formats. A getter (not a ref) so the same
  // toolbar works for any host component that owns its own textarea.
  getTextarea: () => HTMLTextAreaElement | null;
  // Called with the formatted value and the selection to restore.
  onApply: (value: string, selStart: number, selEnd: number) => void;
  disabled?: boolean;
};

// A small Bold / Italic / Underline button group. Uses onMouseDown +
// preventDefault so clicking a button doesn't blur the textarea and lose the
// selection before the format is applied.
export class MarkdownToolbar extends React.Component<ToolbarProps> {
  private apply = (marker: FormatMarker) => {
    const el = this.props.getTextarea();
    if (!el) return;
    const { value, selStart, selEnd } = wrapSelection(el, marker);
    this.props.onApply(value, selStart, selEnd);
  };

  private button(marker: FormatMarker, label: string, css: React.CSSProperties, title: string) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline-secondary"
        disabled={this.props.disabled}
        onMouseDown={e => {
          e.preventDefault();
          this.apply(marker);
        }}
        title={title}
        style={{ minWidth: 34, ...css }}
      >
        {label}
      </Button>
    );
  }

  render() {
    return (
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {this.button('**', 'B', { fontWeight: 'bold' }, 'Bold (Ctrl/Cmd+B)')}
        {this.button('*', 'I', { fontStyle: 'italic' }, 'Italic (Ctrl/Cmd+I)')}
        {this.button('_', 'U', { textDecoration: 'underline' }, 'Underline (Ctrl/Cmd+U)')}
      </div>
    );
  }
}
